import { Suspense } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { fetchWidgets } from 'api/widgets';
import * as NodeBadges from 'assets/js/pages/engage/workflows/components/NodeBadges';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { widgetQueryKeys } from 'queries/widget-query-keys';

const WIDGET_BADGE_LIMIT = 4;

interface WidgetBadgesProps {
    isAnyWidgetSelected: boolean;
    widgetIds: string[];
}
const WidgetBadges = ({ isAnyWidgetSelected, widgetIds }: WidgetBadgesProps) => {
    const { accountId } = useCurrentAccount();

    const params = {
        ids: widgetIds.filter(w => w !== 'any').map(w => Number(w)),
        per_page: widgetIds.length || 1,
    };
    const { data: widgetsData } = useSuspenseQuery({
        queryKey: widgetQueryKeys.widgetsWithFilters(accountId, params),
        queryFn: () => fetchWidgets(accountId, params),
        select: data => {
            return data.data.map(w => ({
                id: w.id.toString(),
                label: w.name ?? w.code ?? '',
                value: w,
            }));
        },
    });

    if (isAnyWidgetSelected) {
        return (
            <NodeBadges.Badge>
                From <span className="text-gray-900">any widget</span>
            </NodeBadges.Badge>
        );
    }

    return widgetsData.length >= WIDGET_BADGE_LIMIT ? (
        <NodeBadges.Badge>
            From <span className="text-gray-900">{widgetsData.length} widgets</span>
        </NodeBadges.Badge>
    ) : (
        <NodeBadges.Badge>
            From{' '}
            <span className="text-gray-900">
                {widgetsData
                    .slice(0, 3)
                    .map(c => c.label)
                    .join(', ')}
            </span>
        </NodeBadges.Badge>
    );
};

const WrappedWidgetBadges = (props: WidgetBadgesProps) => {
    return (
        <Suspense fallback={null}>
            <WidgetBadges {...props} />
        </Suspense>
    );
};

export { WrappedWidgetBadges as WidgetBadges };
